import {
  Button,
  CloseButton,
  Collapse,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useDisclosure,
  VisuallyHidden,
} from '@chakra-ui/react';
import { ErrorMessage } from '@hookform/error-message';
import type { SnapSiteConfigEditFormDataUnvalidated } from '@snap-types/models/snap-site-config';
import type { FieldPathFn } from '@ui/components/form';
import { CircleInfoIcon } from '@ui/components/Icons/CircleInfoIcon';
import { ArrayActions } from '@ui/features/snap-site-config/screens/edit/components/ArrayActions';
import { FormPanel } from '@ui/features/snap-site-config/screens/edit/components/FormPanel';
import { FormPanelHeading } from '@ui/features/snap-site-config/screens/edit/components/FormPanelHeading';
import { FormRowActions } from '@ui/features/snap-site-config/screens/edit/components/FormRowActions';
import {
  type FieldArrayPath,
  get,
  useFieldArray,
  type UseFieldArrayReturn,
  useFormContext,
} from 'react-hook-form';

export function CustomerTermsForm({
  fieldPath,
}: {
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
}) {
  const form = useFormContext<SnapSiteConfigEditFormDataUnvalidated>();

  const defaultAdjustmentFieldPath = fieldPath('defaultAdjustment');

  const customerTermsAdjustmentsFieldPath = fieldPath(
    'customerTermsAdjustments',
  );
  const customerTermsAdjustments = useFieldArray({
    name: customerTermsAdjustmentsFieldPath as FieldArrayPath<SnapSiteConfigEditFormDataUnvalidated>,
    control: form.control,
  });

  return (
    <FormPanel mt={6}>
      <FormPanelHeading>Customer Terms Adjustments</FormPanelHeading>

      <Flex direction="column" px={4} gap={2}>
        <FormControl
          isInvalid={!!get(form.formState.errors, defaultAdjustmentFieldPath)}
        >
          <FormLabel>Default Adjustment:</FormLabel>

          <Input
            {...form.register(defaultAdjustmentFieldPath)}
            id={defaultAdjustmentFieldPath}
            type="number"
            inputMode="decimal"
            step="0.01"
            placeholder="Enter default adjustment"
            min={0}
          />

          <ErrorMessage
            name={defaultAdjustmentFieldPath}
            render={({ message }) => (
              <FormErrorMessage>{message}</FormErrorMessage>
            )}
          />
        </FormControl>

        {customerTermsAdjustments.fields.length === 0 ? <EmptyState /> : null}

        {customerTermsAdjustments.fields.map((item, index) => (
          <FormRow
            key={item.id}
            fieldPath={(fieldName) =>
              fieldPath(`customerTermsAdjustments.${index}.${fieldName}`)
            }
            fieldActions={customerTermsAdjustments}
            index={index}
          />
        ))}

        <FormRowActions>
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              customerTermsAdjustments.append({
                /**
                 * @NOTE(shawk): We want to force the user to pick an appropriate
                 * number of days so we intentionally set the initial value as
                 * nothing (empty string) which we know is invalid. TS is 😡
                 */
                netDays: '' as unknown as number,
                adjustment: '',
              });
            }}
          >
            Add Adjustment
          </Button>
        </FormRowActions>
      </Flex>
    </FormPanel>
  );
}

function EmptyState() {
  const { isOpen, onClose, onToggle } = useDisclosure();

  return (
    <Flex direction="column">
      <Flex
        align="center"
        gap={2}
        p={4}
        rounded="sm"
        bg="snap.teal.50"
        flexWrap="wrap"
      >
        <CircleInfoIcon color="snap.teal.400" />
        <Text fontWeight="bold" fontSize="14px" color="snap.teal.500">
          There are no adjustments set up.
        </Text>

        <Text fontSize="14px">Add an adjustment to get started.</Text>

        <Button
          variant="outline"
          size="xs"
          colorScheme="teal"
          onClick={onToggle}
        >
          What is a Customer Terms Adjustment?
        </Button>
      </Flex>

      <Collapse in={isOpen} animateOpacity>
        <Flex p={4} bg="mw.panelGrey" mt={4} gap={2} rounded="sm">
          <Text>
            Customer Terms Adjustments allow you to apply specific price
            adjustments based on the "Net Days" specified in the customer's
            terms.
          </Text>

          <CloseButton onClick={onClose} />
        </Flex>
      </Collapse>
    </Flex>
  );
}

function FormRow({
  fieldPath,
  fieldActions,
  index,
}: {
  fieldPath: FieldPathFn<SnapSiteConfigEditFormDataUnvalidated>;
  fieldActions: UseFieldArrayReturn<
    SnapSiteConfigEditFormDataUnvalidated,
    `priceStrategies.${number}.priceSegmentConfigs.${number}.customerTermsAdjustments`
  >;
  index: number;
}) {
  const form = useFormContext();

  const netDaysFieldPath = fieldPath('netDays');
  const adjustmentFieldPath = fieldPath('adjustment');

  return (
    <Flex gap={2} align="flex-start">
      <FormControl isInvalid={!!get(form.formState.errors, netDaysFieldPath)}>
        <VisuallyHidden>
          <FormLabel htmlFor={netDaysFieldPath}>Terms Net Days:</FormLabel>
        </VisuallyHidden>

        <InputGroup>
          <Input
            {...form.register(netDaysFieldPath)}
            id={netDaysFieldPath}
            type="number"
            inputMode="numeric"
            placeholder="Enter net days"
            min={1}
          />
          <InputRightElement pr={1} w="auto" px={2}>
            <Text fontSize="sm" opacity={0.5}>
              net days
            </Text>
          </InputRightElement>
        </InputGroup>

        <ErrorMessage
          errors={form.formState.errors}
          name={netDaysFieldPath}
          render={({ message }) => (
            <FormErrorMessage>{message}</FormErrorMessage>
          )}
        />
      </FormControl>

      <FormControl
        isInvalid={!!get(form.formState.errors, adjustmentFieldPath)}
      >
        <VisuallyHidden>
          <FormLabel htmlFor={adjustmentFieldPath}>Adjustment:</FormLabel>
        </VisuallyHidden>

        <Input
          {...form.register(adjustmentFieldPath)}
          id={adjustmentFieldPath}
          type="number"
          inputMode="decimal"
          placeholder="Enter adjustment"
          step="0.01"
          min={0}
        />

        <ErrorMessage
          errors={form.formState.errors}
          name={adjustmentFieldPath}
          render={({ message }) => (
            <FormErrorMessage>{message}</FormErrorMessage>
          )}
        />
      </FormControl>

      <Flex minH="40px" alignItems="center">
        <ArrayActions
          fieldActions={fieldActions}
          index={index}
          layout="horizontal"
        />
      </Flex>
    </Flex>
  );
}
